import React, { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Col, Row } from 'react-bootstrap'

import { Prompt, PromptResult, useChange, useFetch, useFocus } from '../../../Common'
import { useNotifications } from '../../../Notifications'
import { ProfileMsg, useAuth, usePwdChange } from '../../../Auth'
import { Countries, Form, FormContextType, Password, Text } from '../../../Forms'

import { View } from '../../components/View'
import { ComponentHeading } from '../../components/ComponentHeading'
import { ComponentStatus } from '../../components/ComponentStatus'

import { API_URL } from '../../AppConfig'
import { UserAPI, User } from '../../AppTypes'
import { SubscriptionGetResponse } from '../../../../../cz.vegilove.api/src/_api-models'

type PromptShow = 'reset-used' | 'cancel-sub' | 'delete-profile'
type PromptShowMessages = { [ key in PromptShow ]: string | React.ReactNode }

type UserAttrEditable = keyof User
const userAttrEditable: UserAttrEditable[] = [ 'name', 'street', 'city', 'zip', 'country' ]

const transformUserData = ( row: UserAPI ): User => ({ ...row, email: row.email })

export const Profile = () => {

    const intl = useIntl()

    const auth = useAuth<User>()
    const authUser = auth.getUser()

    const { addNotification, removeAllNotifications } = useNotifications()

    const [ pwdChangeRun, pwdChangeRequestState ] = usePwdChange()

    const onFinishPwdChange = ( form: FormContextType ) => pwdChangeRun( form.data[ 'pwd' ] as string )

    const profileDataRequest = useFetch<Partial<User>, User[], UserAPI[]>({ 
        url: `${ API_URL }/user/${ authUser?.contactId || 'XXXX' }`, 
        label: 'fetch-user-data',
        onData: ( data ) => data.map( transformUserData ),
        onSuccess: ( state ) => {
            if( state.options?.label === 'fetch-user-data' && state.success ) {
                setProfileData( state.data && state.data.length > 0 ? state.data[ 0 ] : null )
            }
            if( state.options?.label === 'update-user-data' ) {
                if( state.success ) {
                    addNotification({ variant: 'success', category: 'auth', content: intl.formatMessage({ description: 'auth.profile-change.sucess', defaultMessage: 'Profile has been changed.' }), timeout: 5000 })
                    auth.triggerUserUpdate()
                }
                else
                    addNotification({ variant: 'danger', category: 'auth', content: intl.formatMessage({ description: 'auth.profile-change.error', defaultMessage: 'Profile change did not succeeded.' }) })
            }
        },
        onError: () => {
            addNotification({ variant: 'danger', category: 'auth', content: intl.formatMessage({ description: 'auth.profile-change.error', defaultMessage: 'Profile change did not succeeded.' }) })
        }
    })
    // data need to be cached separately as the request is shared for fetch and update
    const [ profileData, setProfileData ] = useState<User | null>( null )

    const onFinishProfileUpdate = ( form: FormContextType ) => {
        const user = form.data as Partial<User>
        // sanitize proveided data
        const userSanitized = Object.fromEntries( Object.entries( user ).filter( ( val ) => userAttrEditable.includes( val[ 0 ] as UserAttrEditable ) ) )
        removeAllNotifications( 'auth' )
        profileDataRequest.run({ method: 'PUT', label: 'update-user-data', data: userSanitized })
    }

    const [ promptShow, setPromptShow ] = useState<PromptShow | null>( null )
    const promptMsg: PromptShowMessages = useMemo( () => ({
        'reset-used': intl.formatMessage({ description: 'auth.profile.confirm.resetUsed', defaultMessage: 'Do you really want to reset your progress. This operation can not be reverted and your progres will be forever lost.' }),
        'cancel-sub': intl.formatMessage({ description: 'auth.profile.confirm.cancelSub', defaultMessage: 'When clicking Yes, you will stop receiving any emails for this subscription. Your content related to this subscription will stay available for you within your account.' }),
        'delete-profile': intl.formatMessage({ description: 'auth.profile.confirm.deleteProfile', defaultMessage: 'When clicking Yes, your profile will be forever lost! You will not be able to see any content, that you have purchased. This is un-recoverable! Please, think twice before confirming this!' }),
    }), [ intl ])
    

    const profileResetRequest = useChange<Partial<User>>({
        url: `${ API_URL }/contact-content/reset`,
        method: 'PUT',
        label: 'reset-user-data'
    })
    const onProfileResetUsed = () => setPromptShow( 'reset-used' )
    const onProfileResetUsedConfirm = ( result: PromptResult ) => {
        setPromptShow( null )
        removeAllNotifications( 'auth' )
        if( result === 'OKYes' ) profileResetRequest.run({})
    }

    const unregisterRequest = useFetch<never, never, never>({ 
        url: `${ API_URL }/user/unregister/full`,
        label: 'delete', 
        noAutoRun: true,
        onSuccess: ( state ) => {
            removeAllNotifications( 'auth' )
            if( state.success ) {
                addNotification({ variant: 'success', category: 'auth', content: intl.formatMessage({ description: 'auth.profile-delete.sucess', defaultMessage: 'We are very sorry that you are leaving us! Your registration has been deleted. Thank you for using our services.' }), timeout: 8000 })
                auth.logout()
            }
            else
                addNotification({ variant: 'danger', category: 'auth', content: intl.formatMessage({ description: 'auth.profile-delete.error', defaultMessage: 'We are very sorry, but your registration can not be deleted at the moment. Please, contact us via email.' }) })
        },
        onError: () => {
            addNotification({ variant: 'danger', category: 'auth', content: intl.formatMessage({ description: 'auth.profile-delete.error', defaultMessage: 'We are very sorry, but your registration can not be deleted at the moment. Please, contact us via email.' }) })
        }
    } )
    const onProfileUnregister = () => setPromptShow( 'delete-profile' )
    const onProfileUnregisterConfirm = ( result: PromptResult ) => {
        setPromptShow( null )
        if( result === 'OKYes' ) unregisterRequest.run({})
    }

    const [ subs, setSubs ] = useState<SubscriptionGetResponse[]>()
    const nowDt = new Date()
    const [ cancelSub, setCancelSub ] = useState<number>()

    const subsRequest = useFetch<never, SubscriptionGetResponse[], SubscriptionGetResponse[]>({
        url: `${ API_URL }/subscription`,
        label: 'get-subs',
        onData: data => data.map( d => ({ ...d, expiryDt: d.expiryDt ? new Date( d.expiryDt ) : null })),
        onSuccess: state => setSubs( state.data )
    })
    const onProfileUnSubscribe = ( subscriptionId: number ) => {
        setCancelSub( subscriptionId )
        setPromptShow( 'cancel-sub' )
    }
    const onProfileUnSubscribeConfirm = ( result: PromptResult ) => {
        if( !cancelSub ) return
        setPromptShow( null )
        if( result === 'OKYes' ) subsRequest.run({
            url: `${ API_URL }/subscription/cancel/${ cancelSub }`,
            method: 'GET',
            label: 'update-subs-cancel'
        })
        setCancelSub( undefined )
    }

    return (<View>
        <ComponentHeading>
            <FormattedMessage description='auth.pwd-change' defaultMessage='Change Password' />
        </ComponentHeading>
        <ComponentStatus 
            loading={ pwdChangeRequestState.processing } 
            error={ pwdChangeRequestState.isSuccess ? undefined : pwdChangeRequestState.message } 
        />
        <Form 
            formName='pwd-change'
            initialData={{ pwd: '', rePwd: '' }}
            onFinishCallback={ onFinishPwdChange }
        >
            <Password 
                ref={ useFocus }
                controlId='pwd' 
                className='mb-3'
                placeholder={ intl.formatMessage( { description: 'auth.pwd', defaultMessage: 'Password' } ) } 
                minLength={ 8 }
                invalidMsg={ intl.formatMessage({ description: 'auth.pwd-required', defaultMessage: 'Please input your new password! Minimim length is 8 characters.' }) }
            />
            <Password
                controlId='rePwd' 
                className='mb-3'
                placeholder={ intl.formatMessage( { description: 'auth.re-pwd', defaultMessage: 'Repeat Password' } ) } 
                pattern={ ( form: FormContextType ) => form.data[ 'pwd' ] as string }
                invalidMsg={ intl.formatMessage({ description: 'auth.re-pwd-required', defaultMessage: 'Please re-enter exactly your new password!' }) }
            /> 
            <Row className='mb-3'>
                <Col>
                    <Button type='submit' name='pwd-change' className='profile-form-button'>
                        <FormattedMessage description='auth.profile.changePwd' defaultMessage='Change password' />
                    </Button>
                </Col>
            </Row>
        </Form>
        <hr />
        <ComponentHeading>
            { intl.formatMessage( ProfileMsg ) }
        </ComponentHeading>
        <ComponentStatus 
            loading={ profileDataRequest.state.processing } 
            error={ profileDataRequest.state.success ? undefined : profileDataRequest.state.message } 
        />
        <Prompt 
            heading={ intl.formatMessage({ description: 'auth.profile.confirm.heading', defaultMessage: 'Are you sure' }) } 
            message={ promptShow ? promptMsg[ promptShow ] : '' }
            show={ promptShow !== null }
            onClose={ 
                promptShow === 'reset-used' ? onProfileResetUsedConfirm :
                    promptShow === 'delete-profile' ? onProfileUnregisterConfirm :
                        promptShow === 'cancel-sub' ? onProfileUnSubscribeConfirm :
                            () => { return }
            } 
        />
        { profileData && <Form 
            formName='profile'
            initialData={{ 
                name: profileData.name,
                email: profileData.email,
                street: profileData.street,
                city: profileData.city,
                zip: profileData.zip,
                country: profileData.country
            }}
            onFinishCallback={ onFinishProfileUpdate }
        >
            <Text 
                controlId='name' 
                className='mb-3'
                placeholder={ intl.formatMessage({ description: 'auth.name', defaultMessage: 'Name' }) }
                required
                invalidMsg={ intl.formatMessage({ description: 'auth.name-required', defaultMessage: 'Please input your name!' }) }
            />
            <Text 
                controlId='email' 
                type='email'
                className='mb-3'
                disabled
                placeholder={ intl.formatMessage({ description: 'auth.email', defaultMessage: 'Email' }) }
                required
                invalidMsg={ intl.formatMessage({ description: 'auth.email-required', defaultMessage: 'Please input your Email!' }) }
            />
            <Text 
                controlId='street' 
                className='mb-3'
                placeholder={ intl.formatMessage( { description: 'auth.street', defaultMessage: 'Street' } ) } 
                required
                invalidMsg={ intl.formatMessage({ description: 'auth.street-required', defaultMessage: 'Please input street!' }) }
            /> 
            <Row>
                <Col xs="4">
                    <Text 
                        controlId='zip' 
                        className='mb-3'
                        pattern={ ( form: FormContextType ) => form.data[ 'country' ] as string === 'CZE' ? '^(\\d{3} \\d{2})$' : undefined }
                        placeholder={ intl.formatMessage( { description: 'auth.zip', defaultMessage: 'ZIP' } ) } 
                        required
                        invalidMsg={ intl.formatMessage({ description: 'auth.pwd-required', defaultMessage: 'Please input your ZIP code!' }) }
                    /> 
                </Col>
                <Col>
                    <Text 
                        controlId='city' 
                        className='mb-3'
                        placeholder={ intl.formatMessage( { description: 'auth.city', defaultMessage: 'City' } ) } 
                        required
                        invalidMsg={ intl.formatMessage({ description: 'auth.city-required', defaultMessage: 'Please enter your city' }) }
                    /> 
                </Col>
            </Row>
            <Countries 
                apiUrl={ API_URL + '/countries' }
                controlId='country'
                className='mb-3'
                placeholder={ intl.formatMessage( { description: 'auth.country', defaultMessage: 'Country' } ) } 
                required
                invalidMsg={ intl.formatMessage({ description: 'auth.country-required', defaultMessage: 'Please select your country' }) }
            />
            <Row className='mb-3'>
                <Col>
                    <Button type='submit' name='profile-change' className='profile-form-button'>
                        <FormattedMessage description='auth.profile.update' defaultMessage='Update' />
                    </Button>
                </Col>
                <Col className='text-end'>
                    <Button type='button' name='profile-reset' className='profile-form-button' onClick={ onProfileResetUsed }>
                        <FormattedMessage description='auth.profile.resetUsed' defaultMessage='Reset your progress' />
                    </Button>
                </Col>
            </Row>
        </Form> }
        <hr />
        <ComponentHeading>
            <FormattedMessage description='auth.profile.mySubscriptions' defaultMessage='My Subscriptions' />
        </ComponentHeading>
        <ComponentStatus 
            loading={ subsRequest.state.processing } 
            error={ subsRequest.state.success ? undefined : subsRequest.state.message } 
        />
        <div className='mysubs-grid'>
            <div>Created</div>
            <div>Package</div>
            <div>Actions</div>
            { subs && subs.map( s => <React.Fragment key={ s.subscriptionId }>
                <div className='line'></div>
                <div>{ intl.formatDate( s.createdDt ) }</div>
                <div>
                    <div>{ s.package_name }</div>
                    { s.expiryDt && <div>{ 
                        intl.formatMessage({ description: 'auth.profile.mySubscriptions.expires', defaultMessage: 'Expires on' })
                        + ' ' + intl.formatDate( s.expiryDt ) 
                    }</div> }
                </div>
                <div>{
                    !s.canceledDt && (!s.expiryDt || s.expiryDt > nowDt ) && <Button 
                        size="sm" 
                        title={ intl.formatMessage({ description: 'auth.profile.unsubscribeTitle', defaultMessage: 'Stop related emails' })}
                        onClick={ () => onProfileUnSubscribe( s.subscriptionId ) }
                    >{ intl.formatMessage({ description: 'auth.profile.unsubscribeButton', defaultMessage: 'Unsubscribe' }) }</Button>
                }</div>
            </React.Fragment>)}
        </div>
        <hr />
        <Row>
            <Col className='text-end'>
                <Button variant='danger' type='button' name='profile-unregister' className='profile-form-button' onClick={ onProfileUnregister }>
                    <FormattedMessage description='auth.profile.unregister' defaultMessage='Delete my profile' />
                </Button>
            </Col>
        </Row>
    </View>)
}

export default Profile
